import { useMemo, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Link } from "react-router-dom";

import { usePagedList } from "../../hook/usePagedList";
import ClientGroupFilter from "../../components/ui/ClientGroupFilter";
import Pager from "../../components/ui/Pager";
import SeenPill, { unseen } from "../../components/ui/SeenPill";
import { relativeTime, exactTime } from "../../utils/time";
import { useSearchInput } from "../../hook/useSearchInput";

const PER_PAGE_OPTIONS = [10, 25, 50, 100];

/**
 * Person-to-person chats from the MySuhbat app (2026-10-07).
 *
 * STAFF ONLY and read-only: these conversations are private to the two people
 * in the apps, and only this panel -- never a client's cabinet -- shows them.
 * The server enforces that (IsInternalStaff); this page just lists.
 */
const DirectChatsPage = () => {
  const [scope, setScope] = useState({ client: "", group: "" });
  const { input, setInput, search, setSearch } = useSearchInput();
  const [perPage, setPerPage] = useState(10);

  const params = useMemo(
    () => ({
      ...(scope.client ? { client: scope.client } : {}),
      ...(search ? { q: search } : {}),
    }),
    [scope.client, search],
  );

  const { rows, count, loading, error, page, setPage, totalPages } = usePagedList(
    "/direct-chats/",
    params,
    perPage,
  );

  const filtering = Boolean(scope.client || search);
  const from = count === 0 ? 0 : (page - 1) * perPage + 1;
  const to = Math.min(page * perPage, count);

  return (
    <div className="ms-panel">
      <form
        className="ms-toolbar"
        onSubmit={(e) => {
          e.preventDefault();
          setSearch(input.trim());
        }}
      >
        <div className="ms-search">
          <Icon icon="tabler:search" />
          <input
            type="text"
            placeholder="Ism yoki JSHSHIR..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            aria-label="Qidiruv"
          />
        </div>

        {/* Only the client narrows here: a chat belongs to no group. */}
        <ClientGroupFilter
          client={scope.client}
          group=""
          withGroups={false}
          onChange={(s) => setScope({ client: s.client, group: "" })}
        />

        {filtering && (
          <button
            type="button"
            className="ms-btn ms-btn--ghost"
            onClick={() => {
              setScope({ client: "", group: "" });
              setInput("");
              setSearch("");
            }}
          >
            <Icon icon="tabler:x" />
            Tozalash
          </button>
        )}

        <div className="ms-toolbar-spacer" />
        <span className="ms-badge ms-badge--neutral" title="Mijoz kabinetida koʻrinmaydi">
          <Icon icon="tabler:lock" /> faqat xodimlar
        </span>
      </form>

      {loading ? (
        <div className="ms-state">
          <div className="ms-spinner" />
        </div>
      ) : error ? (
        <div className="ms-state">
          <Icon icon="tabler:alert-triangle" />
          <span>{error}</span>
        </div>
      ) : rows.length === 0 ? (
        <div className="ms-state">
          <Icon icon="tabler:messages-off" />
          <span>{filtering ? "Hech narsa topilmadi" : "Shaxsiy suhbatlar yoʻq"}</span>
        </div>
      ) : (
        <>
          <div className="ms-table-wrap">
            <table className="ms-table ms-table--compact">
              <thead>
                <tr>
                  <th>Ishtirokchilar</th>
                  <th style={{ width: 200 }}>Mijoz tizimi</th>
                  <th style={{ width: 110 }}>Xabarlar</th>
                  <th style={{ width: 160 }}>Oxirgi xabar</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => (
                  <tr key={c.id} className={unseen(c) ? "ms-row-unseen" : undefined}>
                    <td className="ms-td-strong ms-td-clamp">
                      <Link className="ms-link" to={`/shaxsiy-suhbatlar/${c.id}`}>
                        {c.member_a?.full_name} ↔ {c.member_b?.full_name}
                      </Link>
                      <SeenPill row={c} />
                    </td>
                    <td className="ms-td-clamp">{c.client_name || "—"}</td>
                    <td className="ms-td-num">{c.message_count ?? 0}</td>
                    <td className="ms-td-muted" title={exactTime(c.last_message_at)}>
                      {c.last_message_at ? relativeTime(c.last_message_at) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="ms-tablefoot">
            <span className="ms-tablefoot-count">
              Koʻrsatilmoqda <b>{from}</b>—<b>{to}</b> / jami <b>{count}</b> ta
            </span>
            <div className="ms-tablefoot-right">
              <label className="ms-perpage">
                <span>Koʻrsatish</span>
                <select
                  className="ms-select ms-select--sm"
                  value={perPage}
                  onChange={(e) => setPerPage(Number(e.target.value))}
                >
                  {PER_PAGE_OPTIONS.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </label>
              <Pager page={page} totalPages={totalPages} onPage={setPage} />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DirectChatsPage;
